import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVisibleAccountRefresh } from "./useVisibleAccountRefresh";

const EMPTY_PAGE = Object.freeze({
  items: [],
  nextCursor: null,
  hasMore: false,
  loading: false,
  loaded: false,
  error: "",
});

const uniqueItems = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item?.id || "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const useAccountPage = ({ enabled, list, query, scopeKey = null, refreshKey = null, refreshWhenVisible = false, preserveOnReload = false, requireStableOrder = false }) => {
  const queryKey = JSON.stringify(query || {});
  const scope = useMemo(() => ({ enabled, list, queryKey, scopeKey }), [enabled, list, queryKey, scopeKey]);
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const [stored, setStored] = useState({ scope, page: EMPTY_PAGE });
  const state = stored.scope === scope ? stored.page : EMPTY_PAGE;
  const stateRef = useRef(state);
  const requestSequence = useRef(0);
  const windowSize = useRef({ scope, pages: 1 });
  stateRef.current = state;
  const publish = useCallback(page => {
    if (activeScope.current !== scope) return;
    stateRef.current = page;
    setStored({ scope, page });
  }, [scope]);

  const load = useCallback(async ({ append = false, background = false } = {}) => {
    if (activeScope.current !== scope) return { ok: false, code: "stale" };
    if (!enabled || typeof list !== "function") return { ok: false, code: "disabled" };
    const current = stateRef.current;
    const pages = windowSize.current.scope === scope ? windowSize.current.pages : 1;
    if (background && (current.loading || pages > 1)) return { ok: true, skipped: true };
    if (append && (current.loading || !current.hasMore || !current.nextCursor)) return { ok: true, skipped: true };
    const sequence = ++requestSequence.current;
    const keep = append || background || (preserveOnReload && current.loaded);
    publish({ ...(keep ? current : EMPTY_PAGE), loading: true, error: "" });
    const isCurrent = () => activeScope.current === scope && sequence === requestSequence.current;
    const targetPages = preserveOnReload && !append && !background ? pages : 1;
    let cursor = append ? current.nextCursor : null, items = [], loadedPages = 0;
    const seenCursors = new Set(cursor ? [cursor] : []);
    let result;
    // Re-read only the window the user opened, using fresh cursors after mutations.
    for (let index = 0; index < targetPages; index += 1) {
      try { result = await list({ ...JSON.parse(queryKey), cursor }); }
      catch { result = { ok: false, error: "This page could not be loaded." }; }
      if (!isCurrent()) return { ok: false, code: "stale" };
      if (!result?.ok) break;
      const page = result.data;
      if (!page || !Array.isArray(page.items) || typeof page.hasMore !== "boolean"
        || (page.hasMore && (typeof page.nextCursor !== "string" || !page.nextCursor || seenCursors.has(page.nextCursor)))) {
        result = { ok: false, code: "invalid_page", error: "The account service returned an invalid page." }; break;
      }
      const combined = [...(append ? current.items : []), ...items, ...page.items];
      if (requireStableOrder && (new Set(combined.map(item => item.id)).size !== combined.length
        || combined.some(item => typeof item.boardRevision !== 'string' || item.boardRevision !== combined[0].boardRevision))) {
        result = { ok: false, code: "task_order_changed", error: "The task order changed. Refresh the list before continuing." }; break;
      }
      items.push(...page.items); loadedPages += 1;
      if (!page.hasMore) break;
      cursor = page.nextCursor; seenCursors.add(cursor);
    }
    if (!result?.ok) {
      const denied = [401, 403, 404].includes(result?.status) || ["access_denied", "authentication_required", "not_found"].includes(result?.code);
      publish({ ...(keep && !denied ? current : EMPTY_PAGE), loading: false, error: result?.error || "This page could not be loaded." });
      return result || { ok: false, code: "request_failed" };
    }
    const page = result.data;
    publish({
      items: uniqueItems(append ? [...current.items, ...items] : items),
      nextCursor: page.nextCursor || null,
      hasMore: page.hasMore,
      loading: false,
      loaded: true,
      error: "",
    });
    windowSize.current = { scope, pages: append ? pages + loadedPages : loadedPages };
    return result;
  }, [enabled, list, queryKey, scope, publish, preserveOnReload, requireStableOrder]);

  useVisibleAccountRefresh(enabled && refreshWhenVisible && state.loaded, () => { void load({ background: true }); });

  useEffect(() => {
    requestSequence.current += 1;
    if (!enabled || typeof list !== "function") {
      publish(EMPTY_PAGE);
      return undefined;
    }
    load();
    return () => { requestSequence.current += 1; };
  }, [enabled, list, load, refreshKey, publish]);

  return {
    ...state,
    reload: () => load(),
    refresh: () => load({ background: true }),
    loadMore: () => load({ append: true }),
  };
};

export default useAccountPage;
